import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import { 
  Users, 
  Plus, 
  X, 
  AlertTriangle, 
  AlertCircle,
  CheckCircle,
  Activity,
  Clock,
  Sparkles,
  RefreshCw,
  TrendingUp,
  Trophy
} from 'lucide-react';
import WorkloadTrendChart from './WorkloadTrendChart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamWorkload = () => {
  const { token } = useAuth();
  const [pinnedWorkloads, setPinnedWorkloads] = useState([]);
  const [workloadTrends, setWorkloadTrends] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [pinnedUserIds, setPinnedUserIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);

  const fetchPinnedWorkloads = useCallback(async () => {
    try {
      const response = await fetch(`${API}/pinned-users/workloads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPinnedWorkloads(data);
      }
    } catch (error) {
      console.error('Error fetching pinned workloads:', error);
    }
  }, [token]);

  const fetchWorkloadTrends = useCallback(async () => {
    try {
      const response = await fetch(`${API}/pinned-users/workloads/trends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const trendsMap = {};
        data.forEach(trend => {
          trendsMap[trend.user_id] = trend;
        });
        setWorkloadTrends(trendsMap);
      }
    } catch (error) {
      console.error('Error fetching workload trends:', error);
    }
  }, [token]);

  const fetchPinnedUserIds = useCallback(async () => {
    try {
      const response = await fetch(`${API}/pinned-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPinnedUserIds(data.pinned_user_ids || []);
      }
    } catch (error) {
      console.error('Error fetching pinned user ids:', error);
    }
  }, [token]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPinnedWorkloads(), fetchPinnedUserIds(), fetchAllUsers(), fetchWorkloadTrends()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPinnedWorkloads, fetchPinnedUserIds, fetchAllUsers, fetchWorkloadTrends]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchPinnedWorkloads(), fetchWorkloadTrends()]);
    setRefreshing(false);
  };

  const handlePinUser = async (userId) => {
    try {
      const response = await fetch(`${API}/pinned-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        await fetchPinnedWorkloads();
        await fetchPinnedUserIds();
        setAddUserOpen(false);
      }
    } catch (error) {
      console.error('Error pinning user:', error);
    }
  };

  const handleUnpinUser = async (userId) => {
    try {
      const response = await fetch(`${API}/pinned-users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setPinnedWorkloads(prev => prev.filter(w => w.user_id !== userId));
        setPinnedUserIds(prev => prev.filter(id => id !== userId));
      }
    } catch (error) {
      console.error('Error unpinning user:', error);
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'medium':
        return <Activity className="w-5 h-5 text-amber-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    }
  };

  const getRiskBadge = (riskLevel) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30',
      medium: 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30',
      low: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
    };
    return styles[riskLevel] || styles.low;
  };

  const availableUsers = allUsers.filter(u => !pinnedUserIds.includes(u.id));

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse text-muted-foreground text-center">
            Loading team workload...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team Workload Monitor
          <Badge variant="outline" className="ml-2">
            <Sparkles className="w-3 h-3 mr-1" />
            AI Powered
          </Badge>
        </CardTitle>
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-workload-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-monitored-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Monitor User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add User to Monitor</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground mb-4">
                Select a team member to monitor their workload on your dashboard
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableUsers.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    All users are already being monitored
                  </p>
                ) : (
                  availableUsers.map(user => (
                    <Button
                      key={user.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handlePinUser(user.id)}
                      data-testid={`pin-user-${user.id}`}
                    >
                      <Avatar className="h-6 w-6 mr-2">
                        <AvatarFallback className="text-xs">
                          {user.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      {user.name}
                    </Button>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {pinnedWorkloads.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="mb-2">No users being monitored</p>
            <p className="text-sm">Click "Monitor User" to add team members</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3">
              {pinnedWorkloads.map((workload) => (
                <div
                  key={workload.user_id}
                  className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md hover:scale-[1.01] ${
                    workload.risk_level === 'critical' ? 'border-red-500/50 bg-red-500/5 hover:bg-red-500/10' :
                    workload.risk_level === 'high' ? 'border-orange-500/50 bg-orange-500/5 hover:bg-orange-500/10' :
                    'bg-card hover:bg-muted/50'
                  }`}
                  data-testid={`workload-card-${workload.user_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {getRiskIcon(workload.risk_level)}
                      <div>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-sm">
                              {workload.user_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{workload.user_name}</p>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${getRiskBadge(workload.risk_level)}`}
                            >
                              {workload.risk_level?.toUpperCase() || 'LOW'} RISK
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => handleUnpinUser(workload.user_id)}
                      data-testid={`unpin-user-${workload.user_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-5 gap-2 mt-3">
                    <div className="text-center p-2 rounded-lg bg-purple-500/10">
                      <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {workload.new_tasks}
                      </p>
                      <p className="text-xs text-muted-foreground">New</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-amber-500/10">
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                        {workload.in_progress_tasks}
                      </p>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-red-500/10">
                      <p className="text-lg font-bold text-red-600 dark:text-red-400">
                        {workload.overdue_tasks}
                      </p>
                      <p className="text-xs text-muted-foreground">Overdue</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-blue-500/10">
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {workload.due_soon_tasks}
                      </p>
                      <p className="text-xs text-muted-foreground">Due Soon</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-emerald-500/10">
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {workloadTrends[workload.user_id]?.completed_this_week || 0}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                        <Trophy className="w-3 h-3" />
                        This Week
                      </p>
                    </div>
                  </div>
                  
                  {/* Workload Trend Chart */}
                  {workloadTrends[workload.user_id]?.history && (
                    <div className="mt-3 p-2 rounded-lg bg-muted/30 border">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                        <TrendingUp className="w-3 h-3" />
                        7-Day Trend
                        <span className="ml-auto flex items-center gap-2">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            Active
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            Completed
                          </span>
                        </span>
                      </div>
                      <WorkloadTrendChart 
                        data={workloadTrends[workload.user_id].history} 
                        height={60}
                      />
                    </div>
                  )}
                  
                  {/* AI Assessment */}
                  {workload.ai_assessment && (
                    <div className="mt-3 p-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
                      <div className="flex items-center gap-2 text-xs font-medium text-violet-600 dark:text-violet-400 mb-1">
                        <Sparkles className="w-3 h-3" />
                        AI Assessment
                      </div>
                      <p className="text-sm text-muted-foreground">{workload.ai_assessment}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamWorkload;
